import React, { FC, useMemo } from "react";
import { FlatList, StyleProp, ViewStyle } from "react-native";
import { Divider } from "@lookiero/aurora";
import { Text } from "@lookiero/sty-psp-ui";
import { Order } from "../../../../domain/entities/order/Order";
import { Product } from "../../../../domain/entities/product/Product";
import { Question } from "../../../../domain/entities/question/Question";
import { ProductItem } from "../productItem/ProductItem";
import { style as productListStyle } from "./ProductList.style";

interface ProductListProps {
  readonly crossed?: boolean;
  readonly currentOrder?: Order; // get it from useStaticInfo (or better, from back)
  readonly products?: Product[];
  readonly questions?: Record<string, Question[]>;
  // oxlint-disable-next-line typescript/no-explicit-any
  readonly selectedItems?: any[];
  readonly showLastUnitsLabel?: boolean;
  readonly showLastUnitsStock?: boolean;
  readonly showNotAvailableLabel?: boolean;
  readonly title?: string;
  readonly rounded?: boolean;
  readonly style?: StyleProp<ViewStyle>;
  readonly onPressCard?: (product: Product, selected: boolean) => void;
  readonly onNext?: () => void;
  readonly onBack?: () => void;
  readonly onDelete?: (product: Product) => void;
  readonly onSelect?: (product: Product, selected: boolean) => void;
  readonly onAddToWishlist?: (product: Product) => void;
  readonly onDeleteFromWishlist?: (product: Product) => void;
  readonly onPressImage?: (product: Product) => void;
}

const ProductList: FC<ProductListProps> = ({
  crossed,
  products,
  selectedItems = [],
  showLastUnitsLabel,
  showLastUnitsStock,
  showNotAvailableLabel,
  title,
  rounded,
  style: customStyle,
  onPressCard,
  onAddToWishlist,
  onDelete,
  onDeleteFromWishlist,
  onPressImage,
  onSelect,
  ...props
}) => {
  const style = useMemo(() => productListStyle(), []);

  return products && products.length ? (
    <FlatList
      ItemSeparatorComponent={() => <Divider style={style.separator} />}
      data={products}
      keyExtractor={(product) => product.itemId as string}
      style={[style.list, rounded ? style.rounded : undefined, customStyle]}
      ListHeaderComponent={() =>
        title ? (
          <Text level={3} style={style.title} testID="product-list-title" variant="action">
            {title}
          </Text>
        ) : null
      }
      renderItem={({ item: product }) => (
        <ProductItem
          crossed={Boolean(crossed)}
          product={product}
          selected={selectedItems.some((item: Product) => product.itemId === item.itemId)}
          showLastUnitsLabel={showLastUnitsLabel}
          showLastUnitsStock={showLastUnitsStock}
          showNotAvailableLabel={showNotAvailableLabel}
          onAddToWishlist={onAddToWishlist}
          onDeleteFromWishlist={onDeleteFromWishlist}
          onPressCard={onPressCard}
          onPressDeleteIcon={onDelete}
          onPressImage={onPressImage}
          onSelect={onSelect}
        />
      )}
      {...props}
    />
  ) : null;
};

export { ProductList };
